import React, { useEffect, useMemo, useState } from 'react';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { ManagementActions } from '../../../Services/_actions/index.js';

interface ProfileDropdownProps {
  value: any;
  onChange: (value: any) => void;
  organisationId: string;
  multiSelect?: boolean;
  label?: string;
}

function mergeProfilesWithSelection(profiles: any[] = [], value: any, multiSelect: boolean) {
  const selected = multiSelect
    ? (Array.isArray(value) ? value : [])
    : (value ? [value] : []);

  if (!selected.length) return profiles;

  const merged = [...profiles];
  const seen = new Set(
    profiles
      .map((profile) => profile?.id)
      .filter((id) => id != null)
      .map((id) => String(id))
  );

  selected.forEach((item) => {
    if (!item || item.id == null || seen.has(String(item.id))) return;
    merged.push(item);
    seen.add(String(item.id));
  });

  return merged;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  value,
  onChange,
  organisationId,
  multiSelect = false,
  label = 'Select Profile',
}) => {
  const actions = ManagementActions();
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchProfiles = async () => {
      setLoading(true);
      try {
        const payload = organisationId
          ? { organisation_id: organisationId, page: 0, search: '', limit: 200 }
          : { page: 0, search: '', limit: 200 };
        const res = await actions.getProfileList(payload);
        if (!cancelled && res?.status === 200) {
          setProfiles(res?.data?.list || []);
        }
      } catch (err) {
        console.error('Failed to load profiles:', err);
        if (!cancelled) setProfiles([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProfiles();

    return () => {
      cancelled = true;
    };
  }, [organisationId]);

  const displayOptions = useMemo(
    () => mergeProfilesWithSelection(profiles, value, multiSelect),
    [multiSelect, profiles, value]
  );

  return (
    <SearchableDropdown
      options={displayOptions}
      value={value}
      onChange={onChange}
      labelKey="name"
      valueKey="id"
      multiSelect={multiSelect}
      label={label}
      loading={loading}
      isOptionEqualToValue={(option, selected) =>
        String(option?.id) === String(selected?.id)
      }
    />
  );
};

export default ProfileDropdown;
